import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";

import { LOADING_STATUSES as STATUS } from "constants/common";
import {
  loadEmailThreads,
  getEmailThreadsData,
  getEmailThreadsStatus,
} from "redux/slices/central-email-threads";
import { getSupportAgents } from "redux/slices/support-agents";
import { getCurrentUser } from "redux/slices/current-user";
import Pagination from "components/pagination";
import { formatCompactAge } from "./format-compact-age";
import {
  findSupportAgent,
  sortSupportAgentsByName,
  supportAgentName,
} from "./support-agent-name";

const ALL = "all";
const MINE = "mine";
// The API reads an assignee of 0 as "nobody".
const UNASSIGNED = "0";

const ThreadList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { threadId } = useParams();

  const emailThreads = useSelector(getEmailThreadsData);
  const status = useSelector(getEmailThreadsStatus);
  const supportAgents = useSelector(getSupportAgents);
  const currentUser = useSelector(getCurrentUser);
  const sortedSupportAgents = useMemo(
    () => sortSupportAgentsByName(supportAgents),
    [supportAgents]
  );

  const [page, setPage] = useState(1);
  const [agentFilter, setAgentFilter] = useState(ALL);

  let supportAgentId = agentFilter;
  if (agentFilter === ALL) supportAgentId = undefined;
  if (agentFilter === MINE) supportAgentId = currentUser?.id;

  useEffect(() => {
    // "Mine" means nothing until the signed in agent is known, and asking
    // without them would list every thread under the wrong heading.
    if (agentFilter === MINE && !currentUser?.id) return;

    dispatch(loadEmailThreads({ page, supportAgentId }));
  }, [dispatch, page, supportAgentId, agentFilter, currentUser?.id]);

  const onPageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const onAgentFilterChange = useCallback((event) => {
    setAgentFilter(event.target.value);
    setPage(1);
  }, []);

  const threads = emailThreads?.data ?? [];

  return (
    <div className="inboxThreadList rightBorder">
      <div className="inboxThreadListHeader">
        <h2 className="inboxThreadListTitle">Inbox</h2>
        <select
          className="inboxSelect inboxThreadListFilter"
          aria-label="Filter by assignee"
          value={agentFilter}
          onChange={onAgentFilterChange}
        >
          <option value={ALL}>All emails</option>
          {currentUser?.id && <option value={MINE}>Assigned to me</option>}
          <option value={UNASSIGNED}>Unassigned</option>
          {!!sortedSupportAgents.length && (
            <optgroup label="Assigned to">
              {sortedSupportAgents.map((agent) => (
                <option key={agent.id} value={String(agent.id)}>
                  {supportAgentName(agent)}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
      {status === STATUS.loading && (
        <div className="inboxThreadListLoader">
          <TailSpin
            width={40}
            height={40}
            color="#E0E0E0"
            ariaLabel="loading"
          />
        </div>
      )}
      {status !== STATUS.loading && !threads.length && (
        <div className="inboxEmptyState">No emails found</div>
      )}
      <div className="inboxThreadListScroll">
        {threads.map((thread) => {
          const attrs = thread.attributes || thread;
          const isActive = String(thread.id) === String(threadId);
          const assignee = findSupportAgent(
            supportAgents,
            attrs.support_agent_id
          );

          return (
            <button
              type="button"
              className={`inboxThreadListItem ${isActive ? "active" : ""}`}
              key={thread.id}
              onClick={() => navigate(`/inbox/${thread.id}`)}
            >
              <div className="inboxThreadListItemTop">
                <span className="inboxThreadListItemFrom">
                  {attrs.from_email}
                </span>
                <span className="inboxThreadListItemAge">
                  {formatCompactAge(attrs.created_at)}
                </span>
              </div>
              <div className="inboxThreadListItemSubject">
                {attrs.subject || "(no subject)"}
              </div>
              {assignee && (
                <div className="inboxThreadListItemAssignee">
                  {supportAgentName(assignee)}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="inboxThreadListPagination">
        <Pagination
          current={page}
          limit={25}
          onPageChanged={onPageChange}
          total={emailThreads?.meta?.pagination?.total ?? null}
        />
      </div>
    </div>
  );
};

export default ThreadList;
